/**
 * O pacote de fontes dos textos sobre a foto (camadas de texto): o registro que **a tela e o servidor
 * leem**. Mora aqui, e não em cada lado, porque os dois têm de escolher o mesmo arquivo de fonte para o
 * mesmo pedido — a tela o carrega por `@font-face` (`/api/fontes/<arquivo>`), o servidor o abre do disco
 * (`apps/api/assets/fontes`) — e uma escolha diferente é o texto impresso diferente do que estava na tela.
 *
 * Tudo aqui é puro (sem I/O, sem DOM). Os arquivos, as licenças (todas SIL OFL 1.1), os commits de
 * origem e os hashes estão no `README.md` da pasta das fontes.
 */
export const ESTILOS_DE_FONTE = [
    "sem-serifa",
    "serifada",
    "manuscrita",
    "impacto",
    "festiva",
];
export const ROTULO_DO_ESTILO_DE_FONTE = {
    "sem-serifa": "Sem serifa",
    serifada: "Serifada",
    manuscrita: "Manuscrita",
    impacto: "Impacto",
    festiva: "Festiva",
};
/** Onde a rota que serve as fontes responde; a tela monta o `@font-face` com isto. */
export const CAMINHO_DAS_FONTES = "/api/fontes";
const variavel = (arquivo, min, max, italico = false) => ({
    arquivo,
    italico,
    peso: { min, max },
});
const estatica = (arquivo, peso, italico = false) => ({
    arquivo,
    italico,
    peso,
});
/**
 * O pacote curado: 13 famílias do Google Fonts (SIL OFL 1.1), de estilos variados. A ordem é a do
 * seletor da tela. Todas têm o português inteiro (acentos, ç, aspas curvas, travessão): ver
 * `caracteresSemGarantia`.
 */
export const FONTES_DE_TEXTO = [
    {
        id: "archivo",
        nome: "Archivo",
        estilo: "sem-serifa",
        pesoPadrao: 700,
        arquivos: [
            variavel("Archivo-VF.ttf", 100, 900),
            variavel("Archivo-Italic-VF.ttf", 100, 900, true),
        ],
    },
    {
        id: "poppins",
        nome: "Poppins",
        estilo: "sem-serifa",
        pesoPadrao: 600,
        // Estática: um arquivo por peso. O itálico só existe no regular e no negrito.
        arquivos: [
            estatica("Poppins-Regular.ttf", 400),
            estatica("Poppins-Medium.ttf", 500),
            estatica("Poppins-SemiBold.ttf", 600),
            estatica("Poppins-Bold.ttf", 700),
            estatica("Poppins-ExtraBold.ttf", 800),
            estatica("Poppins-Italic.ttf", 400, true),
            estatica("Poppins-BoldItalic.ttf", 700, true),
        ],
    },
    {
        id: "fraunces",
        nome: "Fraunces",
        estilo: "serifada",
        pesoPadrao: 600,
        // A mesma família de exibição do design system. `opsz` 48 (título), `SOFT` 30 (tira a dureza das
        // serifas, como na web) e `WONK` 0 (sem as letras "tortas").
        eixosFixos: { opsz: 48, SOFT: 30, WONK: 0 },
        arquivos: [
            variavel("Fraunces-VF.ttf", 100, 900),
            variavel("Fraunces-Italic-VF.ttf", 100, 900, true),
        ],
    },
    {
        id: "playfair-display",
        nome: "Playfair Display",
        estilo: "serifada",
        pesoPadrao: 700,
        arquivos: [
            variavel("PlayfairDisplay-VF.ttf", 400, 900),
            variavel("PlayfairDisplay-Italic-VF.ttf", 400, 900, true),
        ],
    },
    {
        id: "lora",
        nome: "Lora",
        estilo: "serifada",
        pesoPadrao: 600,
        arquivos: [variavel("Lora-VF.ttf", 400, 700), variavel("Lora-Italic-VF.ttf", 400, 700, true)],
    },
    {
        id: "pacifico",
        nome: "Pacifico",
        estilo: "manuscrita",
        pesoPadrao: 400,
        arquivos: [estatica("Pacifico-Regular.ttf", 400)],
    },
    {
        id: "caveat",
        nome: "Caveat",
        estilo: "manuscrita",
        pesoPadrao: 700,
        arquivos: [variavel("Caveat-VF.ttf", 400, 700)],
    },
    {
        id: "dancing-script",
        nome: "Dancing Script",
        estilo: "manuscrita",
        pesoPadrao: 700,
        arquivos: [variavel("DancingScript-VF.ttf", 400, 700)],
    },
    {
        id: "bebas-neue",
        nome: "Bebas Neue",
        estilo: "impacto",
        pesoPadrao: 400,
        arquivos: [estatica("BebasNeue-Regular.ttf", 400)],
    },
    {
        id: "oswald",
        nome: "Oswald",
        estilo: "impacto",
        pesoPadrao: 500,
        arquivos: [variavel("Oswald-VF.ttf", 200, 700)],
    },
    {
        id: "lobster",
        nome: "Lobster",
        estilo: "impacto",
        pesoPadrao: 400,
        arquivos: [estatica("Lobster-Regular.ttf", 400)],
    },
    {
        id: "festive",
        nome: "Festive",
        estilo: "festiva",
        pesoPadrao: 400,
        arquivos: [estatica("Festive-Regular.ttf", 400)],
    },
    {
        id: "fredoka",
        nome: "Fredoka",
        estilo: "festiva",
        pesoPadrao: 600,
        arquivos: [variavel("Fredoka-VF.ttf", 300, 700)],
    },
];
export const FONTE_DE_TEXTO_PADRAO_ID = "archivo";
const PORID = new Map(FONTES_DE_TEXTO.map((f) => [f.id, f]));
export function fonteDeTexto(id) {
    return PORID.get(id);
}
export const ehFonteDeTexto = (id) => PORID.has(id);
/** Todo arquivo que o registro nomeia: a lista **fechada** que a rota das fontes serve, e nada além dela. */
export const ARQUIVOS_DE_FONTE = FONTES_DE_TEXTO.flatMap((f) => f.arquivos.map((a) => a.arquivo));
const ARQUIVOS = new Set(ARQUIVOS_DE_FONTE);
export const ehArquivoDeFonte = (nome) => ARQUIVOS.has(nome);
// ─── Que arquivo, que peso, que estilo ────────────────────────────────────────
/** Se a fonte tem um desenho itálico de verdade. Sem ele, o itálico é ignorado: nada de itálico "falso". */
export const temItalico = (fonte) => fonte.arquivos.some((a) => a.italico);
const faixaDoArquivo = (a) => typeof a.peso === "number" ? { min: a.peso, max: a.peso } : a.peso;
/**
 * Os pesos que a fonte tem no estilo pedido (itálico ou não; se ela não tem itálico, os do normal). O
 * seletor da tela se monta daqui — a fonte de peso único e a estática não oferecem um controle que não
 * muda nada.
 */
export function pesosDaFonte(fonte, italico = false) {
    const arquivos = arquivosDoEstilo(fonte, italico).arquivos;
    const faixas = arquivos.map(faixaDoArquivo);
    const min = Math.min(...faixas.map((f) => f.min));
    const max = Math.max(...faixas.map((f) => f.max));
    const continuo = arquivos.some((a) => typeof a.peso !== "number");
    if (!continuo) {
        return { continuo, min, max, valores: faixas.map((f) => f.min).sort((a, b) => a - b) };
    }
    const valores = [];
    for (let p = Math.ceil(min / 100) * 100; p <= max; p += 100)
        valores.push(p);
    return { continuo, min, max, valores };
}
function arquivosDoEstilo(fonte, italico) {
    const doItalico = fonte.arquivos.filter((a) => a.italico);
    if (italico && doItalico.length > 0)
        return { arquivos: doItalico, italico: true };
    return { arquivos: fonte.arquivos.filter((a) => !a.italico), italico: false };
}
/**
 * O arquivo, o peso e o itálico que valem para o pedido. A tela e o servidor chamam esta função — não
 * repetem a regra —, e por isso o que se vê é o que se imprime.
 *
 * Fonte variável: o arquivo cujo eixo contém o peso; fora de qualquer faixa, o peso é puxado para a
 * borda mais próxima. Fonte estática: o arquivo de peso mais próximo (empate: o mais pesado, que lê
 * melhor sobre uma foto).
 */
export function escolherArquivoDaFonte(fonte, peso, italico) {
    const estilo = arquivosDoEstilo(fonte, italico);
    let melhor;
    for (const arquivo of estilo.arquivos) {
        const { min, max } = faixaDoArquivo(arquivo);
        const efetivo = Math.min(max, Math.max(min, peso));
        const distancia = Math.abs(efetivo - peso);
        if (!melhor ||
            distancia < melhor.distancia ||
            (distancia === melhor.distancia && efetivo > melhor.peso)) {
            melhor = { arquivo, peso: efetivo, distancia };
        }
    }
    if (!melhor)
        throw new Error(`A fonte «${fonte.id}» não tem arquivos.`);
    return { arquivo: melhor.arquivo, italico: estilo.italico, peso: melhor.peso };
}
/**
 * Os eixos que se ajustam na fonte para o arquivo escolhido: o `wght` (só no arquivo variável) e os
 * `eixosFixos` da família. Vazio para o arquivo estático. É o que o servidor passa ao `fontkit` e o que a
 * tela escreve em `font-variation-settings`.
 */
export function eixosDaEscolha(fonte, escolha) {
    if (typeof escolha.arquivo.peso === "number")
        return {};
    return { wght: escolha.peso, ...fonte.eixosFixos };
}
// ─── Para a tela ──────────────────────────────────────────────────────────────
/** O nome da família no CSS. Com prefixo, para não colidir com a Fraunces do design system. */
export const familiaCssDaFonte = (fonte) => `Texto ${fonte.nome}`;
const GENERICA = {
    "sem-serifa": "sans-serif",
    serifada: "serif",
    manuscrita: "cursive",
    impacto: "sans-serif",
    festiva: "sans-serif",
};
/**
 * O CSS de um texto no estilo pedido, pelo mesmo caminho do servidor (`escolherArquivoDaFonte`): o peso
 * e o itálico efetivos, os eixos fixos, e **sem síntese** (`font-synthesis: none`): o navegador não
 * inventa negrito nem itálico para a fonte que não os tem, que é o que o servidor também não faz.
 */
export function estiloCssDaFonte(fonte, peso, italico) {
    const escolha = escolherArquivoDaFonte(fonte, peso, italico);
    const eixos = eixosDaEscolha(fonte, escolha);
    return {
        fontFamily: `"${familiaCssDaFonte(fonte)}", ${GENERICA[fonte.estilo]}`,
        fontWeight: escolha.peso,
        fontStyle: escolha.italico ? "italic" : "normal",
        // O `wght` já vai em `font-weight`; aqui só o resto, e os eixos com letra maiúscula entre aspas.
        fontVariationSettings: Object.entries(eixos)
            .filter(([eixo]) => eixo !== "wght")
            .map(([eixo, valor]) => `"${eixo}" ${valor}`)
            .join(", ") || "normal",
        fontOpticalSizing: "none",
        fontSynthesis: "none",
    };
}
/**
 * As regras `@font-face` de uma fonte (um arquivo, uma regra), apontando para a rota que as serve.
 * `font-display: swap`: o texto aparece na fonte de reserva e troca quando o arquivo chega.
 */
export function regrasFontFace(fonte, base = `${CAMINHO_DAS_FONTES}/`) {
    const familia = familiaCssDaFonte(fonte);
    return fonte.arquivos
        .map((a) => {
        const { min, max } = faixaDoArquivo(a);
        const peso = min === max ? `${min}` : `${min} ${max}`;
        return (`@font-face { font-family: "${familia}"; ` +
            `src: url("${base}${a.arquivo}") format("truetype"); ` +
            `font-weight: ${peso}; font-style: ${a.italico ? "italic" : "normal"}; font-display: swap; }`);
    })
        .join("\n");
}
// ─── O texto que a camada leva ────────────────────────────────────────────────
/**
 * Caracteres de controle e invisíveis que não têm glifo e que ninguém vê: colados de um editor, fariam
 * a fonte parecer incompleta por um caractere que não está na tela.
 */
// eslint-disable-next-line no-control-regex -- os controles são justamente o que se tira
const CONTROLES_E_INVISIVEIS = /[\u0000-\u0008\u000b-\u001f\u007f\u00ad\u200b-\u200d\u2060\ufeff]/g;
/**
 * As linhas do texto como o servidor as desenha, e como a tela deve mostrá-las (`white-space: pre`): a
 * composição canônica (NFC: "ã" é uma letra), sem controles nem invisíveis, tabulação virando espaço,
 * sem espaço nas pontas de cada linha e sem linhas vazias no início nem no fim. Linhas vazias **no meio**
 * ficam: são a respiração que o operador digitou.
 */
export function linhasDoTexto(texto) {
    const linhas = texto
        .normalize("NFC")
        .replace(/\r\n?/g, "\n")
        .replace(/\t/g, " ")
        .replace(CONTROLES_E_INVISIVEIS, "")
        .split("\n")
        .map((l) => l.replace(/^ +| +$/g, ""));
    while (linhas.length > 0 && linhas[0] === "")
        linhas.shift();
    while (linhas.length > 0 && linhas[linhas.length - 1] === "")
        linhas.pop();
    return linhas;
}
/**
 * Os caracteres que **todas** as fontes do pacote têm (a interseção dos 21 arquivos; `fontes-de-texto.test.ts`
 * da API confere cada um contra cada arquivo): o ASCII visível, todo o Latin-1 menos o hífen condicional e
 * o µ, œ Œ š Š ž Ž Ÿ ı ł Ł, travessões, aspas curvas, ‹ ›, † ‡ •, …, ‰, € e ™.
 */
const GARANTIDOS = [
    [0x20, 0x7e],
    [0xa0, 0xac],
    [0xae, 0xb4],
    [0xb6, 0xff],
    [0x131, 0x131],
    [0x141, 0x142],
    [0x152, 0x153],
    [0x160, 0x161],
    [0x178, 0x178],
    [0x17d, 0x17e],
    [0x2013, 0x2014],
    [0x2018, 0x201a],
    [0x201c, 0x201e],
    [0x2020, 0x2022],
    [0x2026, 0x2026],
    [0x2030, 0x2030],
    [0x2039, 0x203a],
    [0x20ac, 0x20ac],
    [0x2122, 0x2122],
];
/** Para o teste conferir o conjunto contra os arquivos. */
export const FAIXAS_DE_CARACTERES_GARANTIDOS = GARANTIDOS;
export function caractereGarantido(caractere) {
    const ponto = caractere.codePointAt(0);
    if (ponto === undefined)
        return true;
    return GARANTIDOS.some(([de, ate]) => ponto >= de && ponto <= ate);
}
/**
 * Os caracteres do texto que **algumas** fontes do pacote podem não ter (emoji, letras fora do português,
 * símbolos): sem garantia de que saiam. Serve para a tela avisar enquanto o operador digita; quem decide é
 * o servidor, que devolve os `glifosAusentes` da fonte escolhida e não desenha um quadradinho no lugar.
 * Sem repetição, na ordem em que aparecem.
 */
export function caracteresSemGarantia(texto) {
    const vistos = new Set();
    for (const linha of linhasDoTexto(texto)) {
        for (const caractere of linha) {
            if (!caractereGarantido(caractere))
                vistos.add(caractere);
        }
    }
    return [...vistos];
}
//# sourceMappingURL=fontes-de-texto.js.map